import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { BarcodeLookupResult } from "@/hooks/api/inventory/useBarcodeLookup";
import { useScanChoiceStore } from "@/stores/useScanChoiceStore";

function describe(match: BarcodeLookupResult) {
  const { item, variant, asset } = match;
  const variantLabel =
    variant && !variant.is_default
      ? variant.option_values.map((ov) => ov.value_text).join(" / ") ||
        variant.sku
      : null;
  if (asset) {
    return {
      kind: "Unit",
      title: asset.asset_identifier,
      detail: [item.name, variantLabel, asset.warehouse_name, asset.status]
        .filter(Boolean)
        .join(" · "),
    };
  }
  if (variant) {
    return {
      kind: "Variant",
      title: `${item.name}${variantLabel ? ` - ${variantLabel}` : ""}`,
      detail: `SKU ${variant.sku}`,
    };
  }
  return { kind: "Item", title: item.name, detail: `SKU ${item.sku}` };
}

function matchKey(match: BarcodeLookupResult) {
  if (match.asset) return `asset-${match.asset.id}`;
  if (match.variant) return `variant-${match.variant.id}`;
  return `item-${match.item.id}`;
}

/** Renders useScanChoiceStore's pending request -- see that store. */
export function ScanMatchChooserDialog() {
  const request = useScanChoiceStore((s) => s.request);
  const settle = useScanChoiceStore((s) => s.settle);

  return (
    <Dialog open={request !== null} onOpenChange={(open) => !open && settle(null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Which one did you scan?</DialogTitle>
          <DialogDescription>
            &ldquo;{request?.code}&rdquo; matches {request?.matches.length}{" "}
            records. Pick the one you meant.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {request?.matches.map((match) => {
            const { kind, title, detail } = describe(match);
            return (
              <button
                key={matchKey(match)}
                type="button"
                onClick={() => settle(match)}
                className="w-full text-left rounded-md border p-3 hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="flex items-center gap-2">
                  <Badge variant="muted" className="text-[10px]">
                    {kind}
                  </Badge>
                  <span className="font-medium text-sm truncate">{title}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 truncate">
                  {detail}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
